import { parseSearchQuery, type SearchOperator } from '@molt/shared';

export type SearchOperatorSuggestionKind =
  | 'board'
  | 'list'
  | 'member'
  | 'label'
  | 'has'
  | 'due'
  | 'created'
  | 'edited'
  | 'is'
  | 'sort';

const SEARCH_OPERATOR_PREFIXES = [
  'board',
  'list',
  'member',
  'label',
  'has',
  'due',
  'created',
  'edited',
  'is',
  'sort',
] as const;

export type SearchOperatorPrefix = (typeof SEARCH_OPERATOR_PREFIXES)[number];

export interface SearchOperatorSuggestion {
  readonly id: string;
  readonly kind: SearchOperatorSuggestionKind;
  readonly label: string;
  readonly token: string;
  readonly description: string;
}

export interface SearchOperatorSummary {
  readonly id: string;
  readonly messageKey: string;
  readonly params?: Record<string, string | number>;
  readonly count?: number;
}

export interface SearchOperatorWarning {
  readonly id: string;
  readonly kind: 'unrecognised' | 'unsupportedValue';
  readonly messageKey:
    'search.operators.warning.unrecognised' | 'search.operators.warning.unsupportedValue';
  readonly params:
    | { readonly token: string }
    | { readonly token: string; readonly prefixToken: `${SearchOperatorPrefix}:` };
}

export interface SearchOperatorFeedback {
  readonly active: readonly SearchOperatorSummary[];
  readonly warnings: readonly SearchOperatorWarning[];
  readonly suggestions: readonly SearchOperatorSuggestion[];
}

export interface SearchSideFilterState {
  readonly board: string | null;
  readonly list: string | null;
  readonly member: string | null;
  readonly label: string | null;
  readonly has: 'any' | 'attachments' | 'description' | 'cover' | 'members' | 'stickers';
  readonly due: 'any' | 'day' | 'week' | 'month' | 'overdue' | 'complete' | 'incomplete';
  readonly created: 'any' | 'day' | 'week' | 'month';
  readonly edited: 'any' | 'day' | 'week' | 'month' | 'year';
  readonly status: 'any' | 'open' | 'archived';
  readonly starredOnly: boolean;
  readonly sort: 'created' | 'updated' | 'due';
}

export const EMPTY_SEARCH_SIDE_FILTER_STATE: SearchSideFilterState = {
  board: null,
  list: null,
  member: null,
  label: null,
  has: 'any',
  due: 'any',
  created: 'any',
  edited: 'any',
  status: 'any',
  starredOnly: false,
  sort: 'created',
};

const PREFIX_SUGGESTIONS: readonly SearchOperatorSuggestion[] = [
  {
    id: 'prefix-board',
    kind: 'board',
    label: 'board:',
    token: 'board:',
    description: 'search.operators.description.prefix-board',
  },
  {
    id: 'prefix-list',
    kind: 'list',
    label: 'list:',
    token: 'list:',
    description: 'search.operators.description.prefix-list',
  },
  {
    id: 'prefix-member',
    kind: 'member',
    label: 'member:',
    token: 'member:',
    description: 'search.operators.description.prefix-member',
  },
  {
    id: 'prefix-label',
    kind: 'label',
    label: 'label:',
    token: 'label:',
    description: 'search.operators.description.prefix-label',
  },
  {
    id: 'prefix-has',
    kind: 'has',
    label: 'has:',
    token: 'has:',
    description: 'search.operators.description.prefix-has',
  },
  {
    id: 'prefix-due',
    kind: 'due',
    label: 'due:',
    token: 'due:',
    description: 'search.operators.description.prefix-due',
  },
  {
    id: 'prefix-created',
    kind: 'created',
    label: 'created:',
    token: 'created:',
    description: 'search.operators.description.prefix-created',
  },
  {
    id: 'prefix-edited',
    kind: 'edited',
    label: 'edited:',
    token: 'edited:',
    description: 'search.operators.description.prefix-edited',
  },
  {
    id: 'prefix-is',
    kind: 'is',
    label: 'is:',
    token: 'is:',
    description: 'search.operators.description.prefix-is',
  },
  {
    id: 'prefix-sort',
    kind: 'sort',
    label: 'sort:',
    token: 'sort:',
    description: 'search.operators.description.prefix-sort',
  },
];

const VALUE_SUGGESTIONS: Record<string, readonly SearchOperatorSuggestion[]> = {
  'label:': [
    {
      id: 'label-name',
      kind: 'label',
      label: 'label:"Needs review"',
      token: 'label:"Needs review"',
      description: 'search.operators.description.label-name',
    },
    {
      id: 'label-colour',
      kind: 'label',
      label: 'label:red',
      token: 'label:red',
      description: 'search.operators.description.label-colour',
    },
  ],
  '#': [
    {
      id: 'label-hash',
      kind: 'label',
      label: '#blocked',
      token: '#blocked',
      description: 'search.operators.description.label-hash',
    },
  ],
  'board:': [
    {
      id: 'board-name',
      kind: 'board',
      label: 'board:"Board name"',
      token: 'board:"Board name"',
      description: 'search.operators.description.board-name',
    },
  ],
  'list:': [
    {
      id: 'list-done',
      kind: 'list',
      label: 'list:Done',
      token: 'list:Done',
      description: 'search.operators.description.list-done',
    },
  ],
  'member:': [
    {
      id: 'member-username',
      kind: 'member',
      label: 'member:username',
      token: 'member:username',
      description: 'search.operators.description.member-username',
    },
  ],
  '@': [
    {
      id: 'member-me',
      kind: 'member',
      label: '@me',
      token: '@me',
      description: 'search.operators.description.member-me',
    },
  ],
  'has:': [
    ...['attachments', 'description', 'cover', 'members', 'stickers'].map(
      (value): SearchOperatorSuggestion => ({
        id: `has-${value}`,
        kind: 'has',
        label: `has:${value}`,
        token: `has:${value}`,
        description: `search.operators.description.has.${value}`,
      }),
    ),
  ],
  'due:': [
    ...['day', 'week', 'month', 'overdue', 'complete', 'incomplete'].map(
      (value): SearchOperatorSuggestion => ({
        id: `due-${value}`,
        kind: 'due',
        label: `due:${value}`,
        token: `due:${value}`,
        description: 'search.operators.description.due',
      }),
    ),
  ],
  'created:': [
    ...['day', 'week', 'month'].map((value): SearchOperatorSuggestion => ({
      id: `created-${value}`,
      kind: 'created',
      label: `created:${value}`,
      token: `created:${value}`,
      description: 'search.operators.description.created',
    })),
  ],
  'edited:': [
    ...['day', 'week', 'month', '365'].map((value): SearchOperatorSuggestion => ({
      id: `edited-${value}`,
      kind: 'edited',
      label: value === '365' ? 'edited:365' : `edited:${value}`,
      token: value === '365' ? 'edited:365' : `edited:${value}`,
      description:
        value === '365'
          ? 'search.operators.description.edited-year'
          : 'search.operators.description.edited',
    })),
  ],
  'is:': [
    ...['starred', 'open', 'archived'].map((value): SearchOperatorSuggestion => ({
      id: `is-${value}`,
      kind: 'is',
      label: `is:${value}`,
      token: `is:${value}`,
      description: 'search.operators.description.status',
    })),
  ],
  'sort:': [
    ...['created', 'edited', 'due', '-edited'].map((value): SearchOperatorSuggestion => ({
      id: `sort-${value}`,
      kind: 'sort',
      label: `sort:${value}`,
      token: `sort:${value}`,
      description: 'search.operators.description.sort',
    })),
  ],
};

const RECOGNISED_PREFIXES = new Set<string>(SEARCH_OPERATOR_PREFIXES);

function tokenise(raw: string): string[] {
  const tokens: string[] = [];
  let current = '';
  let inQuotes = false;

  for (const character of raw) {
    if (character === '"') {
      inQuotes = !inQuotes;
      current += character;
      continue;
    }
    if (!inQuotes && /\s/.test(character)) {
      if (current.length > 0) {
        tokens.push(current);
        current = '';
      }
      continue;
    }
    current += character;
  }

  if (current.length > 0) tokens.push(current);
  return tokens;
}

function tokenStartIndex(raw: string): number {
  let start = 0;
  let inQuotes = false;
  for (let index = 0; index < raw.length; index += 1) {
    const character = raw[index];
    if (character === '"') inQuotes = !inQuotes;
    if (!inQuotes && character !== undefined && /\s/.test(character)) {
      start = index + 1;
    }
  }
  return start;
}

function operatorFromToken(token: string): SearchOperator | null {
  const parsed = parseSearchQuery(token);
  return parsed.freeText.length === 0 && parsed.operators.length === 1
    ? (parsed.operators[0] ?? null)
    : null;
}

function tokenFamily(token: string): keyof SearchSideFilterState | null {
  const operator = operatorFromToken(token);
  if (operator === null) return null;
  switch (operator.kind) {
    case 'board':
      return 'board';
    case 'list':
      return 'list';
    case 'member':
      return 'member';
    case 'label':
      return 'label';
    case 'has':
      return 'has';
    case 'due':
      return 'due';
    case 'created':
      return 'created';
    case 'edited':
      return 'edited';
    case 'sort':
      return 'sort';
    case 'is':
      if (operator.value === 'starred') return 'starredOnly';
      if (operator.value === 'open' || operator.value === 'archived') return 'status';
      return null;
  }
}

function stripFamily(query: string, family: keyof SearchSideFilterState): string {
  return tokenise(query)
    .filter((token) => tokenFamily(token) !== family)
    .join(' ')
    .trim();
}

function appendToken(query: string, token: string | null): string {
  if (token === null) return query.trim();
  return `${query.trim()} ${token}`.trim();
}

function lastOperator<T extends SearchOperator['kind']>(
  operators: readonly SearchOperator[],
  kind: T,
): Extract<SearchOperator, { kind: T }> | undefined {
  for (let index = operators.length - 1; index >= 0; index -= 1) {
    const operator = operators[index];
    if (operator?.kind === kind) {
      return operator as Extract<SearchOperator, { kind: T }>;
    }
  }
  return undefined;
}

function dateSummaryKey(
  family: 'due' | 'created' | 'edited',
  negated: boolean,
  value: Extract<SearchOperator, { kind: 'due' | 'created' | 'edited' }>['value'],
): string {
  const suffix = value.unit === 'days' ? 'days' : value.unit;
  return `search.operators.summary.${family}.${negated ? 'negated.' : ''}${suffix}`;
}

function operatorSummary(operator: SearchOperator, index: number): SearchOperatorSummary {
  const negated = 'negated' in operator && operator.negated;
  const keyPrefix = `search.operators.summary.${operator.kind}`;
  switch (operator.kind) {
    case 'board':
      return {
        id: `${index}:board:${negated ? 'not:' : ''}${operator.value}`,
        messageKey: `${keyPrefix}.${negated ? 'negated' : 'active'}`,
        params: { value: operator.value },
      };
    case 'list':
      return {
        id: `${index}:list:${negated ? 'not:' : ''}${operator.value}`,
        messageKey: `${keyPrefix}.${negated ? 'negated' : 'active'}`,
        params: { value: operator.value },
      };
    case 'member':
      return {
        id: `${index}:member:${negated ? 'not:' : ''}${operator.value}`,
        messageKey: `${keyPrefix}.${negated ? 'negated' : 'active'}`,
        params: { value: operator.value },
      };
    case 'label':
      return {
        id: `${index}:label:${negated ? 'not:' : ''}${operator.value}`,
        messageKey: `${keyPrefix}.${negated ? 'negated' : 'active'}`,
        params: { value: operator.value },
      };
    case 'has':
      return {
        id: `${index}:has:${negated ? 'not:' : ''}${operator.value}`,
        messageKey: `${keyPrefix}.${negated ? 'negated.' : ''}${operator.value}`,
      };
    case 'due':
      return {
        id: `${index}:due:${negated ? 'not:' : ''}${operator.value.unit}${
          operator.value.unit === 'days' ? `:${operator.value.days}` : ''
        }`,
        messageKey: dateSummaryKey('due', negated, operator.value),
        ...(operator.value.unit === 'days'
          ? { params: { count: operator.value.days }, count: operator.value.days }
          : {}),
      };
    case 'created':
      return {
        id: `${index}:created:${negated ? 'not:' : ''}${operator.value.unit}${
          operator.value.unit === 'days' ? `:${operator.value.days}` : ''
        }`,
        messageKey: dateSummaryKey('created', negated, operator.value),
        ...(operator.value.unit === 'days'
          ? { params: { count: operator.value.days }, count: operator.value.days }
          : {}),
      };
    case 'edited':
      return {
        id: `${index}:edited:${negated ? 'not:' : ''}${operator.value.unit}${
          operator.value.unit === 'days' ? `:${operator.value.days}` : ''
        }`,
        messageKey: dateSummaryKey('edited', negated, operator.value),
        ...(operator.value.unit === 'days'
          ? { params: { count: operator.value.days }, count: operator.value.days }
          : {}),
      };
    case 'is':
      return {
        id: `${index}:is:${negated ? 'not:' : ''}${operator.value}`,
        messageKey: `${keyPrefix}.${negated ? 'negated.' : ''}${operator.value}`,
      };
    case 'sort':
      return {
        id: `${index}:sort:${operator.reversed ? 'reverse:' : ''}${operator.field}`,
        messageKey: `${keyPrefix}.${operator.reversed ? 'reversed.' : ''}${
          operator.field
        }`,
      };
  }
}

function currentFragment(query: string): string {
  return query.slice(tokenStartIndex(query)).toLowerCase();
}

function suggestionsForFragment(fragment: string): readonly SearchOperatorSuggestion[] {
  if (fragment.length === 0) return PREFIX_SUGGESTIONS.slice(0, 6);
  if (fragment === '@') return VALUE_SUGGESTIONS['@'] ?? [];
  if (fragment === '#') return VALUE_SUGGESTIONS['#'] ?? [];
  const exact =
    VALUE_SUGGESTIONS[fragment.includes(':') ? `${fragment.split(':')[0]}:` : fragment];
  if (exact !== undefined && fragment.endsWith(':')) return exact;
  const prefix = fragment.split(':')[0] ?? fragment;
  if (fragment.includes(':')) {
    const key = `${prefix}:`;
    return (VALUE_SUGGESTIONS[key] ?? []).filter((suggestion) =>
      suggestion.token.toLowerCase().startsWith(fragment),
    );
  }
  return PREFIX_SUGGESTIONS.filter((suggestion) =>
    suggestion.token.toLowerCase().startsWith(fragment),
  );
}

function isSearchOperatorPrefix(value: string): value is SearchOperatorPrefix {
  return RECOGNISED_PREFIXES.has(value);
}

function warningForToken(token: string, index: number): SearchOperatorWarning | null {
  if (operatorFromToken(token) !== null) return null;
  const rest = token.startsWith('-') ? token.slice(1) : token;
  if (rest.startsWith('#') && rest.length > 1) return null;
  const match = /^([a-z]+):(.*)$/i.exec(rest);
  if (!match) return null;
  const prefix = match[1]!.toLowerCase();
  const value = match[2] ?? '';
  if (!isSearchOperatorPrefix(prefix)) {
    return {
      id: `${index}:unrecognised:${token}`,
      kind: 'unrecognised',
      messageKey: 'search.operators.warning.unrecognised',
      params: { token },
    };
  }
  if (value.length === 0) return null;
  return {
    id: `${index}:unsupportedValue:${token}`,
    kind: 'unsupportedValue',
    messageKey: 'search.operators.warning.unsupportedValue',
    params: { token, prefixToken: `${prefix}:` },
  };
}

export function quoteSearchOperatorValue(value: string): string {
  const trimmed = value.trim().replaceAll('"', '');
  return /\s/.test(trimmed) ? `"${trimmed}"` : trimmed;
}

export function searchOperatorFeedback(query: string): SearchOperatorFeedback {
  const parsed = parseSearchQuery(query);
  return {
    active: parsed.operators.map(operatorSummary),
    warnings: tokenise(query).flatMap((token, index) => {
      const warning = warningForToken(token, index);
      return warning === null ? [] : [warning];
    }),
    suggestions: suggestionsForFragment(currentFragment(query)),
  };
}

export function searchSideFilterState(query: string): SearchSideFilterState {
  const operators = parseSearchQuery(query).operators;
  const board = lastOperator(operators, 'board');
  const list = lastOperator(operators, 'list');
  const member = lastOperator(operators, 'member');
  const label = lastOperator(operators, 'label');
  const has = lastOperator(operators, 'has');
  const due = lastOperator(operators, 'due');
  const created = lastOperator(operators, 'created');
  const edited = lastOperator(operators, 'edited');
  const sort = lastOperator(operators, 'sort');
  const state = { ...EMPTY_SEARCH_SIDE_FILTER_STATE };

  if (board && !board.negated) state.board = board.value;
  if (list && !list.negated) state.list = list.value;
  if (member && !member.negated)
    state.member = member.value === 'me' ? '@me' : member.value;
  if (label && !label.negated) state.label = label.value;
  if (has && !has.negated) state.has = has.value;
  if (due && !due.negated) {
    if (
      due.value.unit === 'day' ||
      due.value.unit === 'week' ||
      due.value.unit === 'month'
    ) {
      state.due = due.value.unit;
    } else if (
      due.value.unit === 'overdue' ||
      due.value.unit === 'complete' ||
      due.value.unit === 'incomplete'
    ) {
      state.due = due.value.unit;
    }
  }
  if (created && !created.negated && created.value.unit !== 'days') {
    state.created = created.value.unit;
  }
  if (edited && !edited.negated) {
    if (edited.value.unit === 'days' && edited.value.days === 365) state.edited = 'year';
    if (edited.value.unit !== 'days') state.edited = edited.value.unit;
  }
  for (let index = operators.length - 1; index >= 0; index -= 1) {
    const operator = operators[index];
    if (operator?.kind !== 'is') continue;
    if (operator.value === 'starred') {
      state.starredOnly = !operator.negated;
      break;
    }
  }
  for (let index = operators.length - 1; index >= 0; index -= 1) {
    const operator = operators[index];
    if (operator?.kind !== 'is') continue;
    if (
      (operator.value === 'open' || operator.value === 'archived') &&
      !operator.negated
    ) {
      state.status = operator.value;
      break;
    }
  }
  if (sort?.field === 'edited') state.sort = 'updated';
  if (sort?.field === 'due') state.sort = 'due';

  return state;
}

export function replaceSearchOperatorToken(
  query: string,
  suggestion: SearchOperatorSuggestion,
): string {
  const start = tokenStartIndex(query);
  return `${query.slice(0, start)}${suggestion.token}`.trim().replace(/\s+/g, ' ');
}

export function withSearchSideFilter(
  query: string,
  patch: Partial<SearchSideFilterState>,
): string {
  let next = query.trim();
  if ('board' in patch) {
    next = stripFamily(next, 'board');
    next = appendToken(
      next,
      patch.board === null || patch.board === undefined || patch.board.trim().length === 0
        ? null
        : `board:${quoteSearchOperatorValue(patch.board)}`,
    );
  }
  if ('list' in patch) {
    next = stripFamily(next, 'list');
    next = appendToken(
      next,
      patch.list === null || patch.list === undefined || patch.list.trim().length === 0
        ? null
        : `list:${quoteSearchOperatorValue(patch.list)}`,
    );
  }
  if ('member' in patch) {
    next = stripFamily(next, 'member');
    const member = patch.member?.trim();
    next = appendToken(
      next,
      member === undefined || member.length === 0
        ? null
        : member === '@me' || member.toLowerCase() === 'me'
          ? '@me'
          : `member:${quoteSearchOperatorValue(member.replace(/^@/, ''))}`,
    );
  }
  if ('label' in patch) {
    next = stripFamily(next, 'label');
    next = appendToken(
      next,
      patch.label === null || patch.label === undefined || patch.label.trim().length === 0
        ? null
        : `label:${quoteSearchOperatorValue(patch.label)}`,
    );
  }
  if ('has' in patch) {
    next = stripFamily(next, 'has');
    next = appendToken(
      next,
      patch.has === undefined || patch.has === 'any' ? null : `has:${patch.has}`,
    );
  }
  if ('due' in patch) {
    next = stripFamily(next, 'due');
    next = appendToken(
      next,
      patch.due === undefined || patch.due === 'any' ? null : `due:${patch.due}`,
    );
  }
  if ('created' in patch) {
    next = stripFamily(next, 'created');
    next = appendToken(
      next,
      patch.created === undefined || patch.created === 'any'
        ? null
        : `created:${patch.created}`,
    );
  }
  if ('edited' in patch) {
    next = stripFamily(next, 'edited');
    next = appendToken(
      next,
      patch.edited === undefined || patch.edited === 'any'
        ? null
        : `edited:${patch.edited === 'year' ? '365' : patch.edited}`,
    );
  }
  if ('status' in patch) {
    next = stripFamily(next, 'status');
    next = appendToken(
      next,
      patch.status === undefined || patch.status === 'any' ? null : `is:${patch.status}`,
    );
  }
  if ('starredOnly' in patch) {
    next = stripFamily(next, 'starredOnly');
    next = appendToken(next, patch.starredOnly === true ? 'is:starred' : null);
  }
  if ('sort' in patch) {
    next = stripFamily(next, 'sort');
    next = appendToken(
      next,
      patch.sort === undefined || patch.sort === 'created'
        ? null
        : `sort:${patch.sort === 'updated' ? 'edited' : 'due'}`,
    );
  }
  return next.trim().replace(/\s+/g, ' ');
}
